import React from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { ReactComponent as CheckBoxUnchecked } from "../../assets/Modal/Rectangle 34624559.svg";
import { ReactComponent as CheckBoxchecked } from "../../assets/Modal/checked-box.svg";
import { useTranslation } from "react-i18next";

interface CommonCheckboxProps {
  label: string;
  fontSize?: string;
  color?: string;
  required?: boolean;
  error?: string;
  checked?: boolean;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  checkedIcon?: React.ReactNode;
  /**
   * When true, reserves the same vertical space as a field label so the
   * checkbox lines up with TextField / Select controls — without using
   * margin that would reflow `items-center` Grid rows.
   * Defaults to true when `label` is non-empty.
   */
  alignWithFieldLabel?: boolean;
}

/** Matches DynamicSelect label row: 14px text (~18px line) + 2px gap */
const FIELD_LABEL_OFFSET_PX = 20;

const CommonCheckbox: React.FC<CommonCheckboxProps> = ({
  label,
  color,
  required,
  error,
  checked = false,
  onChange,
  disabled,
  icon,
  checkedIcon,
  alignWithFieldLabel,
}) => {
  const { t, i18n } = useTranslation();
  const isArabic: boolean = i18n.language === "ar" || i18n.language === "ur";
  const textAlignments: any = isArabic ? "start" : "end";
  const shouldAlignWithField =
    alignWithFieldLabel ?? Boolean(String(label || "").trim());

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignSelf: "flex-start",
        marginTop: "2px",
      }}
    >
      {shouldAlignWithField ? (
        <Box
          aria-hidden
          sx={{
            height: FIELD_LABEL_OFFSET_PX,
            flexShrink: 0,
            pointerEvents: "none",
          }}
        />
      ) : null}
      <FormControlLabel
        labelPlacement={textAlignments}
        className={`pl-[2px] flex items-center ${disabled ? "opacity-50" : "opacity-100"}`}
        sx={{
          m: 0,
          height: 38,
          alignItems: "center",
          alignSelf: "flex-start",
        }}
        label={
          <span
            className="font-Saira font-[600] text-[13px] mt-0 text-txt-color ml-1"
            style={{ color: error && !checked ? "red" : "black" }}
          >
            {t(label)}
            {required && <span className="ml-[5px]">*</span>}
          </span>
        }
        control={
          <Checkbox
            checked={checked}
            onChange={onChange}
            icon={icon || <CheckBoxUnchecked width={"36px"} height={"36px"} />}
            checkedIcon={
              checkedIcon || <CheckBoxchecked width={"36px"} height={"36px"} />
            }
            disabled={disabled && disabled}
            sx={{
              p: 0,
              color: error ? "red" : "#5F6770",
              "& path": { fill: color },
              "& .MuiSvgIcon-root": { fontSize: 35 },
            }}
          />
        }
      />
      {error ? (
        <div className="text-[#FF0000] font-[500] text-[11px] font-Saira italic text-left ml-[10px]">
          {t(error)}
        </div>
      ) : null}
      <span
        className="checkbox checkboxerror"
        style={{ display: "block", height: 0 }}
      />
    </Box>
  );
};

export default CommonCheckbox;
